import {
  escapeAttrValue,
  friendlyAttrValue,
  safeAttrValue,
  type IFilterXSSOptions,
  type IWhiteList,
  type SafeAttrValueHandler
} from "xss";

import { ehqCssProperties } from "./ehq-css-properties";

/**
 * Attributes Sanitize::Config::EHQ applies to every allowed element.
 * ehqv2 lists `rowspanscope`; that is rowspan and scope.
 */
const globalAttributes = [
  "id",
  "title",
  "alt",
  "aria-label",
  "aria-labelledby",
  "aria-hidden",
  "abbr",
  "colspan",
  "rowspan",
  "scope",
  "data",
  "headers",
  "name",
  "rel",
  "role",
  "sorted",
  "target",
  "valign",
  "width",
  "height",
  "bgcolor",
  "style",
  "align",
  "color",
  "class"
];

const attrs = (...extra: string[]): string[] => [...globalAttributes, ...extra];

/**
 * Iframe attributes kept by the EHQ iframe transformer, not the global list.
 * `src` is only useful when the host is in `iframeHosts`.
 */
const iframeAttributes = [
  "id",
  "title",
  "aria-label",
  "aria-labelledby",
  "aria-hidden",
  "name",
  "allowfullscreen",
  "frameborder",
  "height",
  "src",
  "width",
  "marginheight",
  "marginwidth",
  "scrolling",
  "allowautotransparency",
  "role",
  "target",
  "rel",
  "alt"
];

/** Participant Froala tags. Mirrors Sanitize::Config::EHQ elements. */
export const ehqParticipantWhiteList: IWhiteList = {
  a: attrs("href"),
  abbr: attrs(),
  article: attrs(),
  aside: attrs(),
  audio: attrs("src", "controls"),
  area: attrs("href", "shape", "coords"),
  b: attrs(),
  blockquote: attrs("cite", "lang", "data-conversation"),
  br: attrs(),
  button: attrs("type"),
  caption: attrs(),
  circle: attrs("cx", "cy", "r", "fill", "stroke"),
  cite: attrs(),
  code: attrs(),
  colgroup: attrs(),
  dd: attrs(),
  dfn: attrs(),
  div: attrs(),
  dl: attrs(),
  dt: attrs(),
  em: attrs(),
  figcaption: attrs(),
  figure: attrs(),
  font: attrs("size"),
  h1: attrs(),
  h2: attrs(),
  h3: attrs(),
  h4: attrs(),
  h5: attrs(),
  h6: attrs(),
  hr: attrs(),
  i: attrs(),
  iframe: iframeAttributes,
  img: attrs("src", "usemap"),
  kbd: attrs(),
  li: attrs(),
  map: attrs(),
  mark: attrs(),
  ol: attrs(),
  p: attrs(),
  pre: attrs(),
  q: attrs("cite"),
  s: attrs(),
  samp: attrs(),
  section: attrs(),
  small: attrs(),
  span: attrs(),
  strike: attrs(),
  strong: attrs(),
  style: attrs(),
  sub: attrs(),
  sup: attrs(),
  svg: attrs("viewbox", "xmlns", "fill", "stroke"),
  table: attrs("border", "cellspacing", "cellpadding", "summary"),
  tbody: attrs(),
  td: attrs(),
  tfoot: attrs(),
  th: attrs(),
  thead: attrs(),
  time: attrs("datetime", "pubdate"),
  tr: attrs(),
  u: attrs(),
  ul: attrs(),
  var: attrs(),
  video: attrs("src", "controls", "poster")
};

export const ehqCssWhiteList: Record<string, true> = Object.fromEntries(
  ehqCssProperties.map((property) => [property, true])
);

/** Hosts the EHQ iframe transformer keeps. */
const iframeHosts = new Set([
  "www.youtube.com",
  "youtube.com",
  "player.vimeo.com",
  "www.facebook.com",
  "facebook.com",
  "vimeo.com",
  "drive.google.com"
]);

/** Literal matcher so the attribute name is not interpolated into a RegExp. */
const iframeSrcAttribute =
  /(?:^|\s)src\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>]+))/i;

const iframeSrc = (tagHtml: string): string => {
  const match = tagHtml.match(iframeSrcAttribute);

  return (match?.[1] ?? match?.[2] ?? match?.[3] ?? "").trim();
};

export const isAllowedIframeSrc = (src: string): boolean => {
  const trimmed = src.trim();
  if (!trimmed) return false;

  const withProtocol = trimmed.startsWith("//") ? `https:${trimmed}` : trimmed;

  try {
    const url = new URL(withProtocol);
    return (
      (url.protocol === "http:" || url.protocol === "https:") &&
      iframeHosts.has(url.hostname)
    );
  } catch {
    return false;
  }
};

const urlScheme = (value: string): string | null => {
  const match = /^([a-z][a-z0-9+.-]*):/i.exec(value.trim());
  return match ? match[1].toLowerCase() : null;
};

/**
 * Protocols from Sanitize::Config::EHQ.
 * No configured protocol (video, audio) stays on the xss default check.
 */
const allowsUrl = (tag: string, name: string, value: string): boolean => {
  const scheme = urlScheme(value);
  const relative = scheme === null;

  if (tag === "iframe" && name === "src") return isAllowedIframeSrc(value);
  if (tag === "a" && name === "href") {
    return (
      scheme === "http" ||
      scheme === "https" ||
      scheme === "mailto" ||
      scheme === "tel" ||
      relative
    );
  }
  if (tag === "img" && name === "src") {
    return (
      scheme === "http" || scheme === "https" || scheme === "data" || relative
    );
  }
  if (tag === "area" && name === "href") {
    return scheme === "http" || scheme === "https";
  }
  if ((tag === "blockquote" || tag === "q") && name === "cite") {
    return scheme === "http" || scheme === "https";
  }

  return true;
};

const ehqSafeAttrValue: SafeAttrValueHandler = (
  tag,
  name,
  value,
  cssFilter
) => {
  if (tag === "img" && name === "src" && /^\s*data:/i.test(value)) {
    const dataUrl = friendlyAttrValue(value);
    if (/^(?:javascript|vbscript)\s*:/i.test(dataUrl)) return "";
    return escapeAttrValue(dataUrl);
  }

  const cleaned = safeAttrValue(tag, name, value, cssFilter);
  if (!cleaned) return "";
  if (
    (name === "href" || name === "src" || name === "cite") &&
    !allowsUrl(tag, name, cleaned)
  ) {
    return "";
  }

  return cleaned;
};

const keepSpanDataAttributes: IFilterXSSOptions["onIgnoreTagAttr"] = (
  tag,
  name,
  value
) => {
  if (tag === "span" && /^data-[a-z0-9_-]+$/.test(name)) {
    return `${name}="${escapeAttrValue(value)}"`;
  }
};

/** xss options for one sanitize call. The iframe closer flag is per call. */
export const ehqParticipantXssOptions = (): IFilterXSSOptions => {
  let dropIframeClose = false;

  return {
    whiteList: ehqParticipantWhiteList,
    css: { whiteList: ehqCssWhiteList },
    safeAttrValue: ehqSafeAttrValue,
    onIgnoreTagAttr: keepSpanDataAttributes,
    onTag: (tag, html, info) => {
      if (tag !== "iframe") return;
      if (info.isClosing) {
        if (!dropIframeClose) return;
        dropIframeClose = false;
        return "";
      }
      if (isAllowedIframeSrc(iframeSrc(html))) return;
      dropIframeClose = true;
      return "";
    }
  };
};
